import { useRequiredContext } from "@redotech/react-util/context";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext } from "react";
import type { Product, SetupProgress } from "../registry/types";

const QUERY_KEY_BASE = ["merchantSetupProgress"] as const;

function queryKey(teamId: string | undefined): readonly unknown[] {
  return [...QUERY_KEY_BASE, teamId];
}

export interface SetupProgressQueryResult {
  /** Map of product → progress row. */
  progressByProduct: Partial<Record<Product, SetupProgress>>;
  loading: boolean;
  error: unknown;
  /**
   * Write a single returned row into the cache without a follow-up GET.
   * Mutations call this with their RPC return value so the next render
   * sees the new state immediately. Inserts if the product isn't present;
   * replaces if it is.
   */
  applyRowToCache: (row: SetupProgress) => void;
  /**
   * Forces a fresh server fetch. Use only when team-side state changed
   * outside our cache (e.g., merchant connected Stripe in another tab) —
   * mutations should prefer applyRowToCache to avoid the roundtrip.
   */
  refetch: () => Promise<void>;
}

/**
 * Loads merchant setup progress for the current team via the batched RPC
 * and caches it in react-query. Single fetch per (teamId, session)
 * because react-query dedupes by key. Refetches on team switch because
 * the key changes.
 */
export function useSetupProgressQuery(): SetupProgressQueryResult {
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );
  const team = useContext(TeamContext);
  const queryClient = useQueryClient();
  const teamId = team?._id?.toString();

  const query = useQuery({
    queryKey: queryKey(teamId),
    enabled: !!teamId,
    queryFn: async (): Promise<SetupProgress[]> => {
      return platformRpcClient.getMerchantSetupProgressForTeam(null);
    },
    staleTime: Infinity,
  });

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKey(teamId) });
  }, [queryClient, teamId]);

  const applyRowToCache = useCallback(
    (row: SetupProgress) => {
      queryClient.setQueryData<SetupProgress[]>(queryKey(teamId), (prev) => {
        const existing = prev ?? [];
        const idx = existing.findIndex((r) => r.product === row.product);
        if (idx === -1) {
          return [...existing, row];
        }
        const next = existing.slice();
        next[idx] = row;
        return next;
      });
    },
    [queryClient, teamId],
  );

  const progressByProduct: Partial<Record<Product, SetupProgress>> = {};
  for (const row of query.data ?? []) {
    progressByProduct[row.product] = row;
  }

  return {
    progressByProduct,
    loading: query.isLoading,
    error: query.error,
    applyRowToCache,
    refetch,
  };
}
